import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { MessageSquare, Zap, Eye, Archive, Mic, Paperclip, Code, Clock, Check, ArrowRight, ArrowUpRight, Headset, Smartphone, Globe, Bell, MessageCircle, Lock, Edit, Phone, Clock3 } from 'lucide-react';

const features = [
  { icon: Zap, title: 'Instant customer connections', text: 'Engage visitors in real-time with lightning-fast live chat. No switching tabs, no waiting on hold, no email delays. Convert visitors before they leave your site.' },
  { icon: Eye, title: 'Real-time typing preview', text: 'Agents see what customers are typing before they hit send, so replies are ready the moment the question is asked. Prepare accurate responses faster and improve first-response times.' },
  { icon: Clock, title: 'Unlimited agent access', text: 'Your support team has full access to all conversations anytime, anywhere. No message limits or time restrictions for your agents.' },
  { icon: Archive, title: '180-day visitor history', text: 'Site visitors can access their chat history for 180 days in their browser (cookie-based). Continuity even if they switch devices, as long as cookies are preserved.' },
  { icon: Bell, title: 'Custom notification sounds', text: 'Customize notification sounds for both your team and site visitors. Choose from preset sounds or upload custom audio to match your brand and user preferences.' },
  { icon: Edit, title: 'Edit sent messages', text: 'Agents can edit sent messages within 15 minutes of sending. Fix typos, correct information, or improve clarity before the conversation moves forward.' },
  { icon: Phone, title: 'Seamless WhatsApp integration', text: 'Continue conversations from Live Chat to WhatsApp and back without losing context. Smooth handoff between channels with full conversation history preserved.' },
  { icon: MessageCircle, title: 'Email chat transcripts', text: 'Site visitors can request their chat transcript via email. Perfect for record-keeping, compliance, or sharing conversation details with colleagues.' },
  { icon: Clock3, title: 'Custom working hours', text: 'Set custom working hours for your live chat widget. Automatically hide the widget when your team is offline and show custom away messages to visitors.' },
  { icon: Lock, title: 'End-to-end encryption', text: 'All live chat messages are encrypted between your workspace and site visitors. No third-party access. Your customer data stays private and secure.' },
  { icon: Mic, title: 'Voice notes support', text: 'When typing slows things down, agents and customers can both send quick voice notes straight in the chat. Faster communication for complex questions.' },
  { icon: Paperclip, title: 'Multiple file attachments', text: 'Share screenshots, invoices, receipts, and documents without size or format headaches. Support teams and customers both benefit from easy file sharing.' },
  { icon: Code, title: 'Easy 5-minute install', text: 'Drop a few lines of JavaScript on your site and the widget is live. No engineers required, no back-end work, no complicated SDK.' },
];

const installSteps = [
  { step: '1', title: 'Create a workspace', text: 'Sign up for ZuriHub and get your workspace ID from the settings.' },
  { step: '2', title: 'Copy the snippet', text: 'Paste the generated JavaScript snippet into the <head> or <body> of your website.' },
  { step: '3', title: 'Start chatting', text: 'Visitors will see the chat bubble and your agents receive messages in the unified inbox.' },
];

const useCases = [
  { icon: Globe, title: 'E-commerce', text: 'Answer cart, checkout, and shipping questions while shoppers are still browsing.' },
  { icon: Headset, title: 'SaaS support', text: 'Help trials and customers without making them dig through docs or send emails.' },
  { icon: Smartphone, title: 'Services', text: 'Capture booking, quote, and appointment requests from mobile visitors instantly.' },
];

const faqs = [
  { q: 'Do I need a developer to install the live chat widget?', a: 'No. ZuriHub gives you a small JavaScript snippet that can be pasted into any website, CMS, or landing-page builder. Most teams are live in under five minutes.' },
  { q: 'How long are chat messages stored for agents vs visitors?', a: 'Agents have unlimited access to all their conversations anytime. Site visitors can access their chat history for 180 days in their browser (cookie-based), as long as they don\'t clear cookies.' },
  { q: 'Can I edit messages after sending them?', a: 'Yes. Agents can edit sent messages within 15 minutes of sending. This allows you to fix typos, correct information, or improve clarity before the conversation moves forward.' },
  { q: 'Can visitors continue to WhatsApp from live chat?', a: 'Yes. Seamlessly continue conversations from Live Chat to WhatsApp and back without losing context. Full conversation history is preserved across channels for smooth handoff.' },
  { q: 'Can visitors request email transcripts?', a: 'Yes. Site visitors can request their chat transcript via email. This is perfect for record-keeping, compliance requirements, or sharing conversation details with colleagues.' },
  { q: 'Is the live chat mobile-friendly?', a: 'Yes. The ZuriHub live chat widget is fully responsive and works on all modern mobile browsers and devices. The widget adapts to screen size for optimal mobile experience.' },
  { q: 'Are chat messages encrypted?', a: 'Yes. All live chat messages are encrypted end-to-end between your workspace and site visitors. No third-party access. Your customer data stays private and secure.' },
  { q: 'Can I set custom working hours?', a: 'Yes. Set custom working hours for your live chat widget. The widget automatically hides when your team is offline and shows custom away messages to visitors during your off-hours.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'ZuriHub Live Chat',
    description: 'Fast live chat widget with real-time typing preview, 180-day message history, voice notes, and multiple file attachments.',
    url: `${site.baseUrl}/live-chat`,
    brand: { '@type': 'Brand', name: 'ZuriHub' },
    offers: {
      '@type': 'Offer',
      url: `${site.baseUrl}/live-chat`,
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to install ZuriHub Live Chat on your website',
    step: installSteps.map((s) => ({ '@type': 'HowToStep', name: s.title, text: s.text })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function LiveChat() {
  return (
    <>
      <SEO
        title="Live Chat Widget - Real-Time Customer Support for Websites"
        description="Add a fast live chat widget to your website in minutes. Features include custom working hours, WhatsApp integration, email transcripts, message editing, end-to-end encryption, and 180-day visitor history. Mobile-friendly and easy 5-minute install."
        path="/live-chat"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="live chat widget, website chat, customer support chat, real-time chat, WhatsApp chat integration, mobile live chat, encrypted chat, custom working hours, email chat transcripts, free live chat, AI chat widget, sales chat, support chat"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-3 py-1 text-sm font-semibold text-brand-300">
                <MessageSquare className="h-4 w-4" /> Live Chat
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Real-time customer support <span className="gradient-text">right on your website</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300 lg:mx-0">
                A powerful live chat widget with custom working hours, WhatsApp integration, email transcripts, and end-to-end encryption. Install in 5 minutes and start converting visitors before they leave.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
                >
                  Add live chat <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#install"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/60 px-7 py-3.5 text-base font-bold text-slate-200 transition hover:bg-slate-800"
                >
                  See how it works <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
              <img
                src="https://media.getzurihub.com/Jayrah%20Assets/Hero_Messaging__en_us.avif"
                alt="ZuriHub live chat widget interface"
                className="rounded-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="features-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="features-heading" className="section-title">Built for speed and context</h2>
            <p className="section-subtitle mx-auto mt-3">Everything your team needs to reply faster and convert more visitors.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="install-heading" id="install">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 id="install-heading" className="section-title">Install in three steps</h2>
              <p className="mt-3 text-slate-600">No complicated SDK. No back-end work. Just copy, paste, and chat.</p>
              <div className="mt-8 space-y-4">
                {installSteps.map((s) => (
                  <div key={s.step} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-extrabold text-brand-700">{s.step}</div>
                    <div>
                      <h4 className="font-bold text-slate-900">{s.title}</h4>
                      <p className="text-sm text-slate-600">{s.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-900 p-6 shadow-xl">
              <div className="flex items-center gap-2 text-slate-300">
                <Code className="h-5 w-5" />
                <span className="text-sm font-semibold">JavaScript snippet</span>
              </div>
              <pre className="mt-4 overflow-x-auto rounded-2xl bg-slate-950 p-5 text-sm text-slate-300">
                <code>{`<script>
  window.zuriChatConfig = {
    workspaceId: 'your-workspace-id',
    position: 'bottom-right',
    color: '#041dd7',
    enableAI: true
  };
</script>
<script async
  src="https://media.getzurihub.com/widget.js"
  data-api="https://zurichat-api.sales-f51.workers.dev"
  data-workspace="your-workspace-id">
</script>`}</code>
              </pre>
              <p className="mt-4 text-xs text-slate-400">Replace <code className="text-brand-400">your-workspace-id</code> with the ID from your ZuriHub settings.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900 text-white" aria-labelledby="use-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="use-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Works for every use case</h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">From online stores to SaaS apps and service businesses.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-2xl border border-slate-800 bg-slate-800/50 p-6">
                  <Icon className="h-8 w-8 text-brand-400" />
                  <h3 className="mt-4 font-bold">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-300">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="trust-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm sm:p-10">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h2 id="trust-heading" className="section-title">Reliable and secure</h2>
                <p className="mt-3 text-slate-600">The live chat widget runs on the same Zuri Cloud edge network that powers every other channel.</p>
                <ul className="mt-6 space-y-3">
                  {[
                    'End-to-end encryption between workspace and visitors',
                    '99.99% uptime SLA on Enterprise',
                    'GDPR-ready data handling',
                    'Auto-scales with visitor volume',
                    'Custom working hours with auto-hide widget',
                    '15-minute message editing window',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col justify-center rounded-2xl bg-brand-50 p-8 text-center">
                <Clock className="mx-auto h-10 w-10 text-brand-700" />
                <p className="mt-4 text-3xl font-extrabold text-slate-900">180 days</p>
                <p className="mt-1 text-slate-600">of chat history retained so you never lose customer context.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Live chat FAQs</h2>
          <p className="section-subtitle mx-auto mt-4">Quick answers to common questions.</p>
          <div className="mt-10 space-y-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm group">
                <summary className="flex cursor-pointer list-none items-center justify-between text-left font-semibold text-slate-900">
                  {faq.q}
                  <span className="ml-4 transition group-open:rotate-180">&#9662;</span>
                </summary>
                <p className="mt-3 text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start chatting with visitors today</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Create a free ZuriHub workspace and add live chat to your site in minutes.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Get started free <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
